import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';

const Reports = () => {
  const [stats, setStats] = useState({
    totalUsers: 0, totalInvitations: 0,
    categoryBreakdown: {}, occasionBreakdown: {}
  });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => { loadReports(); }, [period]);

  const loadReports = async () => {
    try {
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      const startDate = new Date(Date.now() - days * 86400000).toISOString();

      const [
        { count: totalUsers }, { count: totalInvitations },
        { data: invitations }, { data: users }
      ] = await Promise.all([
        supabase.from('user_profiles').select('*', { count: 'exact', head: true }),
        supabase.from('invitations').select('*', { count: 'exact', head: true }),
        supabase.from('invitations').select('category, occasion, created_at').gte('created_at', startDate),
        supabase.from('user_profiles').select('created_at').gte('created_at', startDate)
      ]);

      const categoryBreakdown = {};
      invitations?.forEach(inv => { categoryBreakdown[inv.category] = (categoryBreakdown[inv.category] || 0) + 1; });
      const occasionBreakdown = {};
      invitations?.forEach(inv => { occasionBreakdown[inv.occasion] = (occasionBreakdown[inv.occasion] || 0) + 1; });

      setStats({
        totalUsers: totalUsers || 0, totalInvitations: totalInvitations || 0,
        categoryBreakdown, occasionBreakdown,
        invitationsInPeriod: invitations?.length || 0, usersInPeriod: users?.length || 0
      });
    } catch (err) {
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const categoryChartOption = {
    tooltip: { trigger: 'item', backgroundColor: '#fff', borderColor: '#e5e7eb', textStyle: { color: '#374151' } },
    legend: { bottom: '0%', textStyle: { color: '#6b7280' } },
    series: [{
      type: 'pie', radius: '65%',
      data: Object.entries(stats.categoryBreakdown).map(([name, value]) => ({
        name, value,
        itemStyle: { color: name === 'ask-out' ? '#ec4899' : name === 'couples' ? '#a855f7' : '#f43f5e' }
      })),
      label: { show: true, formatter: '{b}: {c}', color: '#6b7280' },
      itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 3 }
    }]
  };

  const occasionChartOption = {
    tooltip: { trigger: 'axis', backgroundColor: '#fff', borderColor: '#e5e7eb', textStyle: { color: '#374151' } },
    grid: { left: '8%', right: '4%', top: '8%', bottom: '20%' },
    xAxis: {
      type: 'category', data: Object.keys(stats.occasionBreakdown).slice(0, 10),
      axisLabel: { rotate: 45, fontSize: 10, color: '#6b7280' },
      axisLine: { lineStyle: { color: '#e5e7eb' } }
    },
    yAxis: {
      type: 'value', axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f3f4f6' } }, axisLabel: { color: '#6b7280' }
    },
    series: [{
      data: Object.values(stats.occasionBreakdown).slice(0, 10), type: 'bar', barWidth: '50%',
      itemStyle: {
        borderRadius: [8, 8, 0, 0],
        color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [{ offset: 0, color: '#ec4899' }, { offset: 1, color: '#f43f5e' }]
        }
      }
    }]
  };

  const summaryCards = [
    { label: 'Total Users', value: stats.totalUsers, icon: 'Users', color: 'from-blue-500 to-blue-600', sub: `+${stats.usersInPeriod} in period` },
    { label: 'Total Invitations', value: stats.totalInvitations, icon: 'Mail', color: 'from-pink-500 to-rose-500', sub: `+${stats.invitationsInPeriod} in period` },
    { label: 'Avg Invitations/User', value: stats.totalUsers > 0 ? (stats.totalInvitations / stats.totalUsers).toFixed(1) : 0, icon: 'TrendingUp', color: 'from-purple-500 to-purple-600', sub: 'Lifetime average' },
    { label: 'Active Categories', value: Object.keys(stats.categoryBreakdown).length, icon: 'Activity', color: 'from-emerald-500 to-emerald-600', sub: 'In use' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
          <p className="text-gray-500 mt-1">Detailed insights into DayClap performance</p>
        </div>
        <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-1">
          {['7d', '30d', '90d'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                period === p
                  ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className={`w-14 h-14 bg-gradient-to-br ${card.color} rounded-2xl flex items-center justify-center shadow-lg mb-5`}>
              <SafeIcon name={card.icon} className="text-white text-2xl" />
            </div>
            <p className="text-4xl font-bold text-gray-900">{card.value}</p>
            <p className="text-sm font-medium text-gray-500 mt-1">{card.label}</p>
            <p className="text-xs text-green-600 font-medium mt-2">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-gray-800 text-lg">By Category</h3>
            <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">Period</span>
          </div>
          <ReactECharts option={categoryChartOption} style={{ height: 320 }} />
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-gray-800 text-lg">Top Occasions</h3>
            <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">Period</span>
          </div>
          <ReactECharts option={occasionChartOption} style={{ height: 320 }} />
        </div>
      </div>
    </div>
  );
};

export default Reports;